let portalCounter = 0;

export function createRowMenu(root, options = {}) {
    const config = {
        portalClass: "row-menu-portal",
        moreText: "...",
        moreWidth: 60,
        //maxWidth: "300px",
        resizeDelay: 50,
        transitionDuration: 300,
        closeDelay: 80,
        offsetTop: 8,
        viewportGap: 8,
        activeClass: "is-active",
        ...options
    };
    // a unique portal per instance, unless the caller pins an explicit id to share one
    if (!config.portalId) {
        config.portalId = `${config.portalClass}-${++portalCounter}`;
    }
    let items = Array.from(root.children);
    let moreBtn, moreList, timer, closeTimer, leaveTimer;
    const portal = getPortal();
    let activeLi = null;

    // ===== PORTAL =====
    function getPortal() {
        let el = document.getElementById(config.portalId);
        if (!el) {
            el = document.createElement("div");
            el.id = config.portalId;
            el.style.cssText = "position:fixed;top:0;left:0;z-index:2;pointer-events:none;";
            document.body.appendChild(el);
        }
        // styling is class-based now, so the id can be unique per instance
        el.classList.add(config.portalClass);
        // drive the CSS height/content transition duration from the config
        el.style.setProperty("--row-menu-transition", `${config.transitionDuration}ms`);
        if (config.maxWidth) {
            el.style.maxWidth = config.maxWidth;
        }
        return el;
    }

    // ===== HELPERS =====
    function getInitialLi() {
        return root.querySelector(":scope > li");
    }

    function setPortalPosition(li) {
        const rect = li.getBoundingClientRect();
        const gap = config.viewportGap;
        const portalWidth = portal.getBoundingClientRect().width;

        // align to the item's left edge, but keep the portal inside the viewport
        let left = rect.left;
        const maxLeft = window.innerWidth - gap - portalWidth;
        if (left > maxLeft) left = maxLeft; // would overflow the right edge -> pull left
        if (left < gap) left = gap;         // never go past the left edge

        const top = rect.bottom + config.offsetTop;
        portal.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    }

    // highlight the top-level item whose submenu is currently shown in the portal
    function setActive(li) {
        if (li) li.classList.add(config.activeClass);
    }

    function clearActive(li) {
        if (li) li.classList.remove(config.activeClass);
    }

    function bindNestedSubmenus(container) {
        container.querySelectorAll("li").forEach(li => {
            const submenu = li.querySelector(":scope > ul");
            if (!submenu) return;

            li.onmouseenter = () => {
                submenu.style.left = "";
                submenu.style.right = "";

                requestAnimationFrame(() => {
                    const rect = submenu.getBoundingClientRect();
                    if (rect.right > window.innerWidth) {
                        submenu.style.left = "auto";
                        submenu.style.right = "100%";
                    } else {
                        submenu.style.left = "100%";
                        submenu.style.right = "auto";
                    }
                });
            };
        });
    }

    function reset() {
        if (moreBtn) {
            root.append(...moreList.children);
            moreBtn.remove();
            moreBtn = null;
            moreList = null;
        }
    }

    function layout() {
        reset();

        items = Array.from(root.children);

        const tempBtn = document.createElement("li");
        tempBtn.className = "row-menu-more";
        tempBtn.innerHTML = `<a href="#">${config.moreText}</a>`;
        root.appendChild(tempBtn);
        const moreBtnWidth = tempBtn.getBoundingClientRect().width;
        tempBtn.remove();

        const limit = root.getBoundingClientRect().right - moreBtnWidth;

        items.forEach(item => {
            if (item.getBoundingClientRect().right > limit) {
                if (!moreBtn) {
                    moreBtn = document.createElement("li");
                    moreBtn.className = "row-menu-more";
                    moreBtn.innerHTML = `
                        <a href="#">${config.moreText}</a>
                        <ul class="row-menu-more-list"></ul>
                    `;
                    root.appendChild(moreBtn);
                    moreList = moreBtn.querySelector("ul");
                }
                moreList.appendChild(item);
            }
        });

        bind();
    }

    // ===== HOVER LOGIC =====
    function cancelClose() {
        clearTimeout(leaveTimer);
        leaveTimer = null;
    }

    // Close shortly after the pointer leaves both the menu bar and the portal.
    // The :hover check at fire time is the single source of truth, so we never
    // get stuck open because of an unexpected (or null) relatedTarget.
    function scheduleClose() {
        cancelClose();
        leaveTimer = setTimeout(() => {
            leaveTimer = null;
            if (!root.matches(":hover") && !portal.matches(":hover")) {
                close();
            }
        }, config.closeDelay);
    }

    function bind() {
        root.querySelectorAll(":scope > li").forEach(li => {
            li.onmouseenter = () => open(li);
        });

        // keep open while hovering the bar or the portal, close on leaving both
        root.onmouseenter = cancelClose;
        root.onmouseleave = scheduleClose;
        portal.onmouseenter = cancelClose;
        portal.onmouseleave = scheduleClose;
    }

    // measure the natural height of the current content and animate to it
    function setPortalHeight() {
        const current = portal.style.height;
        portal.style.height = "auto";
        const target = portal.getBoundingClientRect().height;
        portal.style.height = current;
        void portal.offsetHeight; // force reflow so the height change animates
        portal.style.height = `${target}px`;
    }

    // move whatever submenu is in the portal back to its owner <li>;
    // never wipe the portal if there's nowhere to put the content back
    function detachSubmenu(li) {
        if (!li) return;
        const submenu = portal.querySelector(":scope > ul");
        if (submenu) li.appendChild(submenu);
        portal.innerHTML = "";
    }

    function open(li) {
        cancelClose();

        // re-opening the same menu mid-close: its submenu is still in the portal
        const submenu = li === activeLi
            ? portal.querySelector(":scope > ul")
            : li.querySelector(":scope > ul");

        // a top-level item without a submenu just closes the current one (with fade)
        if (!submenu) {
            close();
            return;
        }

        // cancel a pending close so the content stays in the portal
        clearTimeout(closeTimer);
        closeTimer = null;

        // switching from another menu: put the previous submenu back right away
        if (activeLi && activeLi !== li) {
            detachSubmenu(activeLi);
            clearActive(activeLi);
        }

        activeLi = li;
        setActive(li);

        if (submenu.parentElement !== portal) {
            portal.innerHTML = "";
            portal.appendChild(submenu);
        }
        portal.style.pointerEvents = "auto";
        portal.classList.add("is-open");

        setPortalPosition(li);
        setPortalHeight();

        bindNestedSubmenus(portal);
    }

    function close() {
        if (!activeLi || closeTimer) return;

        const li = activeLi;

        clearActive(li);
        portal.style.pointerEvents = "none";
        portal.classList.remove("is-open");

        // keep the content in the portal until the fade-out finishes,
        // then move the submenu back to its original <li>
        closeTimer = setTimeout(() => {
            detachSubmenu(li);
            portal.style.height = "";
            activeLi = null;
            closeTimer = null;
        }, config.transitionDuration);
    }

    function onResize() {
        clearTimeout(timer);
        timer = setTimeout(() => {
            layout();
            const targetLi = activeLi || getInitialLi();
            if (targetLi) setPortalPosition(targetLi);
            if (activeLi) setPortalHeight();
        }, config.resizeDelay);
    }

    function init() {
        layout();
        window.addEventListener("resize", onResize);

        const firstLi = getInitialLi();
        if (firstLi) setPortalPosition(firstLi);
    }

    init();

    return {
        destroy() {
            window.removeEventListener("resize", onResize);
            clearTimeout(closeTimer);
            clearTimeout(leaveTimer);
            closeTimer = null;
            leaveTimer = null;
            if (activeLi) {
                detachSubmenu(activeLi);
                clearActive(activeLi);
                activeLi = null;
            }
            portal.classList.remove("is-open");
            portal.style.pointerEvents = "none";
            portal.style.height = "";
            portal.onmouseenter = null;
            portal.onmouseleave = null;
            root.onmouseenter = null;
            root.onmouseleave = null;
            reset();
        },
        update: layout
    };
}